import React from 'react';
import PropTypes from 'prop-types';
import Sparkline from '../../../../components/Sparkline/Sparkline';
import { EM_DASH, formatCapAware, formatNumber } from './queueMonitorFormat';

/**
 * PerQueueTable
 * Overview-tab per-queue breakdown. Columns are locked by the v1 contract (6.7):
 * Queue | Pending | Processing | Oldest processing | Failed (24h UTC) | Pending trend (48h)
 *
 * - Pending/Processing are legacy capped counts (25001 renders "25,000+").
 * - byQueue24h missing a queue's key = 0; the whole object absent (old backend) = em dash.
 * - Trend sparkline reads series.points[].pending_by_queue; queues absent from a point
 *   count as 0 (the sampler omits empty queues).
 * - Rows sort known queues first, in fixed order, then unknown queues alphabetically.
 */

const KNOWN_QUEUE_ORDER = [
	'default',
	'scraper',
	'low',
	'high',
	'notifications',
	'broadcasts',
];

const sortQueueNames = (names) => {
	const known = KNOWN_QUEUE_ORDER.filter((name) => names.includes(name));
	const unknown = names
		.filter((name) => !KNOWN_QUEUE_ORDER.includes(name))
		.sort((a, b) => a.localeCompare(b));
	return [...known, ...unknown];
};

const PerQueueTable = ({ queues, byQueue24h, seriesPoints }) => {
	const names = sortQueueNames(Object.keys(queues || {}));
	const points = Array.isArray(seriesPoints) ? seriesPoints : [];

	if (names.length === 0) {
		return <p className="text-muted per-queue-empty">No queue data yet</p>;
	}

	return (
		<div className="table-responsive per-queue-table">
			<table className="table table-sm table-hover">
				<thead className="table-light">
					<tr>
						<th>Queue</th>
						<th className="text-end">Pending</th>
						<th className="text-end">Processing</th>
						<th>Oldest processing</th>
						<th className="text-end">Failed (24h UTC)</th>
						<th>Pending trend (48h)</th>
					</tr>
				</thead>
				<tbody>
					{names.map((name) => {
						const queue = queues[name] || {};
						return (
							<tr key={name}>
								<td>
									<code className="small">{name}</code>
								</td>
								<td className="text-end">{formatCapAware(queue.pending)}</td>
								<td className="text-end">{formatCapAware(queue.processing)}</td>
								<td>
									{queue.reservedAtOldest && queue.reservedAtOldest.durationHuman
										? queue.reservedAtOldest.durationHuman
										: EM_DASH}
								</td>
								<td className="text-end">
									{byQueue24h === null || byQueue24h === undefined
										? EM_DASH
										: formatNumber(byQueue24h[name] !== undefined ? byQueue24h[name] : 0)}
								</td>
								<td>
									<Sparkline
										points={points.map((point) =>
											point && point.pending_by_queue && point.pending_by_queue[name] !== undefined
												? point.pending_by_queue[name]
												: 0,
										)}
										width={90}
										height={24}
										ariaLabel={`${name} pending trend`}
									/>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

PerQueueTable.propTypes = {
	queues: PropTypes.object.isRequired,
	byQueue24h: PropTypes.object,
	seriesPoints: PropTypes.array,
};

PerQueueTable.defaultProps = {
	byQueue24h: undefined,
	seriesPoints: undefined,
};

export default PerQueueTable;
